/**
 * Plays a product card's video on hover, and not a moment before.
 *
 * Ported from the lazy category videos in ../foamico-main/index.html. The
 * point is the loading, not the playing: a collection of sixteen cards would
 * pull sixteen video files on load if the markup carried a src, so the src is
 * held in data-src and moved across the first time a pointer arrives.
 *
 * Only pointers that hover get this. On a touch screen `hover` is not a thing
 * a finger does — the card is tapped and navigates — so the video would either
 * never play or fire on the way to a tap, spending someone's data on a file
 * they will not see. The poster frame is what they get, which is the same
 * picture the video opens on.
 *
 * Guarded, because more than one section on a page can render product cards —
 * a PDP has related products, a collection page has its grid. The browser
 * fetches a `<script src>` once but *executes* every occurrence, and an
 * unguarded top-level class is redeclared and throws.
 */
(() => {
  if (customElements.get('card-video')) return;

class CardVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video || !window.matchMedia('(hover: hover)').matches) return;

    // An animation nobody asked for is exactly what this setting turns off.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);

    // Bound on the card rather than the video: the pointer is over the title
    // as often as the picture, and the whole card is one link.
    this.card = this.closest('.product-card') || this;
    this.card.addEventListener('pointerenter', this.onEnter);
    this.card.addEventListener('pointerleave', this.onLeave);
  }

  disconnectedCallback() {
    this.card?.removeEventListener('pointerenter', this.onEnter);
    this.card?.removeEventListener('pointerleave', this.onLeave);
  }

  onEnter() {
    const { src } = this.video.dataset;
    if (src && !this.video.src) this.video.src = src;

    // play() rejects if the pointer leaves before the file is ready, which is
    // not a failure worth reporting — the card simply stays a picture.
    this.video.play().then(
      () => this.classList.add('is-playing'),
      () => {}
    );
  }

  onLeave() {
    this.classList.remove('is-playing');
    this.video.pause();
    // Back to the first frame, so the next hover starts the loop over rather
    // than resuming halfway through a rotation.
    this.video.currentTime = 0;
  }
}

customElements.define('card-video', CardVideo);
})();
